import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { InsightBar, statusColor } from '../../components/charts/InsightBar.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';
import { countBy, sumBy } from '../../utils/insightStats.js';

const STATUS_ORDER = ['DRAFT', 'POSTED'];

export function CreditNotesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState('');
  const { data: antwort, isLoading, error } = useQuery({
    queryKey: ['finance', 'credit-notes', 'list', typeFilter],
    queryFn: () => financeApi.listCreditNotes(typeFilter ? { type: typeFilter } : undefined),
    placeholderData: keepPreviousData,
  });
  const data = antwort?.items || [];
  const meta = antwort?.meta;

  const palette = useChartPalette();
  const stats = useMemo(() => {
    const rows = data || [];
    const counts = countBy(rows, (r) => r.status);
    const statuses = [
      ...STATUS_ORDER.filter((s) => counts[s]),
      ...Object.keys(counts).filter((s) => !STATUS_ORDER.includes(s)),
    ];
    return {
      count: rows.length,
      statuses,
      amountByStatus: statuses.map((s) => sumBy(rows, (r) => r.totalAmount, (r) => r.status === s)),
      drafts: counts.DRAFT || 0,
      total: sumBy(rows, (r) => r.totalAmount),
      postedSum: sumBy(rows, (r) => r.totalAmount, (r) => r.status === 'POSTED'),
    };
  }, [data]);

  const post = useMutation({
    mutationFn: (id) => financeApi.postCreditNote(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['finance', 'credit-notes'] }),
  });

  const columns = [
    { key: 'creditNoteNumber', label: t('col.number'), render: (r) => <Link to={`/finance/credit-notes/${r.id}/print`}>{r.creditNoteNumber}</Link> },
    { key: 'creditDate', label: t('col.date'), render: (r) => formatDate(r.creditDate) },
    {
      key: 'type', label: t('creditNote.col.type'),
      render: (r) => (
        <Badge variant={r.type === 'PURCHASE' ? 'neutral' : 'info'}>
          {t(`creditNote.type.${r.type || 'SALES'}`)}
        </Badge>
      ),
    },
    { key: 'invoice', label: t('creditNote.print.forInvoice'), render: (r) => <Link to={`/finance/invoices/${r.invoiceId}`}>{r.invoiceId.slice(0, 8)}</Link> },
    { key: 'totalAmount', label: t('col.total'), align: 'right', render: (r) => formatCurrency(r.totalAmount, r.currency) },
    { key: 'status', label: t('col.status'), render: (r) => <Badge status={r.status} /> },
    {
      key: 'actions', label: '',
      render: (r) => (r.status === 'DRAFT'
        ? <Button size="sm" variant="primary" disabled={post.isPending} onClick={() => post.mutate(r.id)}>{t('finance.invoices.action.post')}</Button>
        : <Link to={`/finance/credit-notes/${r.id}/print`}><Button size="sm" variant="default">{t('invoice.print.printOrSave')}</Button></Link>),
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('creditNote.title')}</h1>
          <p className="muted">{t('creditNote.subtitle')}</p>
        </div>
      </header>

      {/* Die Kennzahlen summieren die geholten Zeilen. Sind das nicht alle,
          ist jede Zahl darunter zu klein. */}
      {meta?.total > data.length && (
        <div className="alert warning">
          {t('insight.gekuerzt', { gezeigt: data.length, gesamt: meta.total })}
        </div>
      )}
      {stats.count > 0 && (
        <InsightBar
          title={t('insight.byStatus')}
          labels={stats.statuses.map((s) => (s === '-' ? s : t(`status.${s}`)))}
          values={stats.amountByStatus}
          colors={stats.statuses.map((s) => statusColor(palette, s))}
          formatValue={(n) => formatCurrency(n)}
          metrics={[
            { label: t('insight.count'), value: stats.count },
            { label: t('status.DRAFT'), value: stats.drafts },
            { label: t('insight.total'), value: formatCurrency(stats.total) },
            { label: t('creditNote.insight.postedSum'), value: formatCurrency(stats.postedSum) },
          ]}
        />
      )}
      <div className="toolbar">
        <select
          className="select"
          aria-label={t('creditNote.col.type')}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">{t('creditNote.typeFilter.all')}</option>
          <option value="SALES">{t('creditNote.type.SALES')}</option>
          <option value="PURCHASE">{t('creditNote.type.PURCHASE')}</option>
        </select>
      </div>
      {error && <div className="alert danger">{error.message}</div>}
      <Table columns={columns} rows={data} isLoading={isLoading} error={error} meta={meta} emptyMessage={t('creditNote.empty')} onRowClick={(r) => navigate(`/finance/credit-notes/${r.id}/print`)} />
    </section>
  );
}
